/**
 * Where to go after signing in (UX-063). A crew lead who taps a job link from
 * a text and has to sign in first should land on that job, not on Home.
 *
 * The target travels as `?next=/jobs/123` on /login. Only same-origin, in-app
 * paths are accepted: anything absolute, protocol-relative ("//evil.com"),
 * backslash-tricked or pointing back at a signed-out page falls back to Home.
 */
import { can } from './permissions'

export const DEFAULT_AFTER_SIGN_IN = '/dashboard'

const SIGNED_OUT_PAGES = ['/login', '/forgot-password', '/reset-password', '/change-password', '/verify-email-change', '/revert-email-change']

export function safeNextPath(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_AFTER_SIGN_IN
  let value = raw.trim()
  try {
    // A doubly-encoded value would otherwise slip "//" past the checks below.
    if (/%2f|%5c/i.test(value)) value = decodeURIComponent(value)
  } catch {
    return DEFAULT_AFTER_SIGN_IN
  }
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return DEFAULT_AFTER_SIGN_IN
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f]/.test(value)) return DEFAULT_AFTER_SIGN_IN
  try {
    const url = new URL(value, 'https://app.invalid')
    if (url.origin !== 'https://app.invalid') return DEFAULT_AFTER_SIGN_IN
    if (SIGNED_OUT_PAGES.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`))) return DEFAULT_AFTER_SIGN_IN
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return DEFAULT_AFTER_SIGN_IN
  }
}

/**
 * Where someone lands when no `next` was asked for. Crew (sees the schedule but
 * no money) goes straight to today's jobs on the calendar; everyone else Home.
 */
export function homePathFor(user: Parameters<typeof can>[0]): string {
  if (user && !can(user, 'viewMoney') && can(user, 'viewCalendar')) return '/calendar'
  return DEFAULT_AFTER_SIGN_IN
}

/** The `next` target, or the person's own landing page when none was asked for. */
export function afterSignInPath(rawNext: string | null | undefined, user: Parameters<typeof can>[0]): string {
  const next = safeNextPath(rawNext)
  return next === DEFAULT_AFTER_SIGN_IN && !rawNext ? homePathFor(user) : next
}

/**
 * Seconds to wait after a 429 from a sign-in or reset endpoint, from the
 * Retry-After header (the limits are per minute). Defaults to 60.
 */
export function retryAfterSeconds(err: unknown): number {
  const raw = (err as { response?: { headers?: Record<string, unknown> } } | null)?.response?.headers?.['retry-after']
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.min(Math.ceil(n), 600) : 60
}

/** `/login?next=…` for the page the person was trying to open. */
export function loginPathFor(path: string): string {
  const next = safeNextPath(path)
  return next === DEFAULT_AFTER_SIGN_IN ? '/login' : `/login?next=${encodeURIComponent(next)}`
}
